/**
 * Vincent B906 (Eric 2026-02-14 SPEC STRICK PROD) — Page complète de
 * résultats de recherche par tags. Utilisée quand Vincent presse Enter
 * dans la sidebar avec ≥1 tag actif : navigate vers cette page pour
 * revoir des dizaines de matches côte à côte.
 *
 * Query params :
 *   - tags=a,b,c  (obligatoire, sinon page vide)
 *   - mode=and|or (défaut : and, comme B904)
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Hash, Wrench, ArrowsMerge, Cloud, ClockCounterClockwise, MagicWand, MagnifyingGlass } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { canAccessAdmin } from "../utils/permissions";
import TagsPage from "./TagsPage";
import TagCloud from "../components/TagCloud"; // Vincent B1100
import CsvImportModal from "../components/CsvImportModal"; // Vincent B1011
import ImportHistoryModal from "../components/ImportHistoryModal"; // Vincent B1012
import { useLang } from "../contexts/LanguageContext";

const API = process.env.REACT_APP_BACKEND_URL;

// Vincent B1100 (Eric 2026-09-07) — Les fonctions tags deviennent de VRAIS
// onglets (`?tab=`) au lieu de rediriger ailleurs ou de dérouler la section
// de fusion en bas de page : « Détecter les doublons », « Purger les
// orphelins », « Gérer le vocabulaire » et « Nuage de tags » s'affichent
// désormais chacun dans son onglet, sans quitter la page.
const TAB_KEYS = ["resultats", "doublons", "orphelins", "vocabulaire", "nuage"];

export default function SearchResultsPage() {
  // Tâche 1372 (Eric 2026-06-30) — Page entièrement française.
  const { lang, tr } = useLang();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = canAccessAdmin(user);

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialTags = useMemo(
    () => (params.get("tags") || "").split(",").map((t) => t.trim()).filter(Boolean),
    [params],
  );
  const initialMode = params.get("mode") === "or" ? "or" : "and";
  const activeTab = TAB_KEYS.includes(params.get("tab") || "") ? params.get("tab") : "resultats";

  const [tags, setTags] = useState(initialTags);
  const [mode, setMode] = useState(initialMode);
  // Vincent B1006 (Eric 2026-02-26 SPEC STRICK PROD) — Empty state
  // onboarding sur la toolbar tags quand l'org n'a AUCUN tag encore.
  // Fetch simple sur /tags/org-vocabulary au mount pour connaître le
  // total sans forcer l'ouverture du panneau embarqué.
  const [orgTagCount, setOrgTagCount] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/tags/org-vocabulary`, { withCredentials: true });
        if (!cancelled) setOrgTagCount((data?.tags || []).length);
      } catch { if (!cancelled) setOrgTagCount(0); }
    })();
    return () => { cancelled = true; };
  }, []);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [addingTag, setAddingTag] = useState("");
  // Vincent B910 (Eric 2026-02-14 SPEC STRICK PROD) — Tags connexes.
  const [related, setRelated] = useState([]);
  // Vincent B1011 (Eric 2026-02-26 SPEC STRICK PROD) — Modale d'import
  // CSV massif de tags sur vidéos existantes. Réservée aux admins.
  const [showTagsImportModal, setShowTagsImportModal] = useState(false);
  // Vincent B1012 (Eric 2026-02-26 SPEC STRICK PROD) — Historique +
  // rollback des imports tags.
  const [showTagsImportHistoryModal, setShowTagsImportHistoryModal] = useState(false);
  const submitTagsImport = async (payload) => {
    const { data } = await axios.post(
      `${API}/api/videos/tags/import`,
      payload,
      { withCredentials: true },
    );
    if (data?.mode === "vocabulary") {
      toast.success(`${data.added || 0} tag(s) ajouté(s) au vocabulaire, ${data.updated || 0} mis à jour.`);
    } else if (data?.added > 0) toast.success(`${data.added} tag(s) importé(s) sur ${data.videos_updated} vidéo(s).`);
    else if (data?.skipped_missing > 0) toast.info(`Aucune vidéo mise à jour (${data.skipped_missing} tag(s) sur vidéos inconnues).`);
    return data;
  };

  const syncUrl = useCallback((nextTags, nextMode, nextTab) => {
    const p = new URLSearchParams();
    if (nextTags.length > 0) p.set("tags", nextTags.join(","));
    p.set("mode", nextMode);
    if (nextTab && nextTab !== "resultats") p.set("tab", nextTab);
    navigate({ pathname: "/dashboard/search", search: p.toString() }, { replace: true });
  }, [navigate]);
  // Vincent B1100 — Changement d'onglet : on garde les tags/mode courants dans
  // l'URL pour que le retour à « Résultats » ne perde pas la recherche.
  const goToTab = useCallback((key) => {
    const p = new URLSearchParams();
    if (tags.length > 0) p.set("tags", tags.join(","));
    p.set("mode", mode);
    if (key !== "resultats") p.set("tab", key);
    navigate({ pathname: "/dashboard/search", search: p.toString() });
  }, [navigate, tags, mode]);

  const runSearch = useCallback(async (curTags, curMode) => {
    if (!curTags || curTags.length === 0) {
      setResults({ items: [], total: 0, mode: curMode, tags: [] });
      setRelated([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/search/by-tags`, {
        params: { tags: curTags.join(","), mode: curMode, limit: 500 },
        withCredentials: true,
      });
      setResults(data);
      // Vincent B907 (Eric 2026-02-14) — Log fire-and-forget sur la page complète.
      axios.post(
        `${API}/api/search/tag-events`,
        { tags: curTags, mode: curMode, results_count: data?.total || 0 },
        { withCredentials: true },
      ).catch(() => { /* silencieux */ });
      // Vincent B910 (Eric 2026-02-14) — Charge les tags connexes en //.
      axios.get(`${API}/api/tags/related`, {
        params: { tags: curTags.join(","), mode: curMode, limit: 12 },
        withCredentials: true,
      })
        .then(({ data: rel }) => setRelated(rel?.related || []))
        .catch(() => setRelated([]));
    } catch (err) {
      toast.error(tr("Erreur : ", "Error: ") + (err?.response?.data?.detail || err.message));
      setResults(null);
      setRelated([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Vincent B1100 — On ne lance la recherche que sur l'onglet Résultats :
    // inutile d'appeler /search/by-tags quand l'utilisateur consulte le nuage
    // ou purge des orphelins.
    if (activeTab === "resultats") runSearch(tags, mode);
  }, [tags, mode, runSearch, activeTab]);

  const addTag = (t) => {
    const name = (t || "").trim();
    if (!name) return;
    if (tags.some((s) => s.toLowerCase() === name.toLowerCase())) return;
    const next = [...tags, name];
    setTags(next);
    setAddingTag("");
    syncUrl(next, mode, activeTab);
  };
  const removeTag = (t) => {
    const next = tags.filter((s) => s !== t);
    setTags(next);
    syncUrl(next, mode, activeTab);
  };
  const switchMode = () => {
    const next = mode === "and" ? "or" : "and";
    setMode(next);
    syncUrl(tags, next, activeTab);
  };

  const items = results?.items || [];

  return (
    <div className="max-w-7xl mx-auto p-6" data-testid="search-results-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">{tr("🔎 Résultats de recherche", "🔎 Search results")}</h1>
          <p className="text-sm text-zinc-600 mt-1" data-testid="search-results-summary">
            {loading
              ? tr("Recherche…", "Searching…")
              : (items.length === 0
                  ? tr(`Aucun résultat pour ${tags.length} tag(s)`, `No result for ${tags.length} tag(s)`)
                  : tr(
                      `${items.length} document(s) — mode ${mode === "and" ? "ET (tous les tags)" : "OU (au moins un)"}`,
                      `${items.length} document(s) — ${mode === "and" ? "AND mode (every tag)" : "OR mode (at least one)"}`,
                    ))}
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-xs font-semibold px-3 py-1.5 rounded bg-white border border-zinc-300 hover:bg-zinc-50"
          data-testid="search-results-back-btn"
        >
          {tr("← Retour", "← Back")}
        </button>
      </div>

      {/* Vincent B1006 (Eric 2026-02-26 SPEC STRICK PROD) — Empty state
          onboarding avant la toolbar quand l'org n'a aucun tag encore.
          Guide clairement l'utilisateur vers la 1re action pour éviter
          la sensation de « rien ne se passe » sur les fonctions tags. */}
      {orgTagCount === 0 && (
        <div
          className="mb-4 rounded-lg border border-dashed border-indigo-300 bg-indigo-50 p-4"
          data-testid="tags-empty-onboarding"
          role="status"
        >
          <p className="text-sm font-semibold text-indigo-900 mb-1">
            {tr("🎬 Commence par… ajouter des tags à tes vidéos !", "🎬 Start by… adding tags to your videos!")}
          </p>
          <p className="text-xs text-indigo-800/90 leading-relaxed">
            {tr(
              "Ton organisation n'a encore aucun tag. Les tags aident les apprenants à retrouver rapidement du contenu par thème. Ouvre une vidéo, clique sur « Modifier » et ajoute 2-3 mots-clés — le nuage de tags, les suggestions de fusion et la recherche par thème s'activeront automatiquement.",
              "Your organisation has no tag yet. Tags help learners find content by topic quickly. Open a video, click “Edit” and add 2-3 keywords — the tag cloud, the merge suggestions and topic search will switch on by themselves.",
            )}
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Link
              to="/dashboard/videos"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700"
              data-testid="tags-empty-onboarding-cta"
            >
              {tr("Ouvrir mes vidéos →", "Open my videos →")}
            </Link>
          </div>
        </div>
      )}

      {/* Vincent B1100 (Eric 2026-09-07) — VRAIS onglets. Avant : « Nuage de
          tags » redirigeait vers le tableau de bord, « Gérer le vocabulaire »
          vers /dashboard/tags, et « Détecter doublons » / « Purger les
          orphelins » dépliaient la section de fusion en bas de page via des
          manipulations de DOM. Désormais chaque fonction a son onglet, sans
          quitter la page ni chercher une section repliée. */}
      <nav
        className="mb-4 flex flex-wrap items-center gap-1 border-b border-zinc-200"
        data-testid="search-results-tabs"
        aria-label={tr("Fonctions de gestion des tags", "Tag management functions")}
        role="tablist"
      >
        {[
          // Tâche 1429 — Les info-bulles étaient restées en français : elles
          // sont le seul endroit qui dise à quoi sert chaque onglet, donc les
          // laisser en français revenait à réserver l'explication aux
          // francophones.
          { key: "resultats", label: tr("Résultats", "Results"), icon: MagnifyingGlass,
            title: tr("Les documents correspondant aux tags recherchés",
              "The documents matching the searched tags") },
          { key: "doublons", label: tr("Détecter les doublons", "Detect duplicates"), icon: MagicWand,
            title: tr("Doublons exacts et suggestions de fusion sémantiques",
              "Exact duplicates and semantic merge suggestions") },
          { key: "orphelins", label: tr("Purger les orphelins", "Purge orphans"), icon: ArrowsMerge,
            title: tr("Les tags utilisés une seule fois — à purger ou renommer",
              "Tags used only once — to purge or rename") },
          { key: "vocabulaire", label: tr("Gérer le vocabulaire", "Manage the vocabulary"), icon: Hash,
            title: tr("Tous les tags de l'organisation : renommer, fusionner, historique",
              "Every tag in the organisation: rename, merge, history") },
          { key: "nuage", label: tr("Nuage de tags", "Tag cloud"), icon: Cloud,
            title: tr("Le nuage de tags pondéré par usage, sans quitter la page",
              "The tag cloud weighted by usage, without leaving the page") },
        ].map(({ key, label, icon: Icon, title }) => {
          const active = activeTab === key;
          return (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => goToTab(key)}
              title={title}
              className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 -mb-px border-b-2 transition-colors ${
                active
                  ? "border-[#002FA7] text-[#002FA7] bg-white"
                  : "border-transparent text-zinc-600 hover:text-zinc-900 hover:bg-zinc-50"
              }`}
              data-testid={`search-tab-${key}`}
            >
              <Icon size={13} weight="regular" />
              {label}
            </button>
          );
        })}
      </nav>

      {/* Barre d'outils secondaire (ex-toolbar B1002) : actions admin
          (règles d'auto-merge, imports CSV et leur historique). Les 4 anciens
          raccourcis vers d'autres pages sont devenus les onglets ci-dessus. */}
      <nav
        className="mb-4 flex flex-wrap items-center gap-2 bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-lg p-2"
        data-testid="search-results-toolbar"
        aria-label={tr("Actions avancées sur les tags", "Advanced tag actions")}
      >
        <span className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider px-2">
          {tr("Fonctions tags :", "Tag functions:")}
        </span>
        {isAdmin && (
          <Link
            to="/dashboard/admin/tag-merges"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-400"
            data-testid="search-toolbar-btn-auto-merge"
            title={tr("Configurer les règles d'auto-merge (concepteur/admin)", "Configure the auto-merge rules (designer/admin)")}
          >
            <Wrench size={13} weight="regular" />
            {tr("Règles auto-merge", "Auto-merge rules")}
          </Link>
        )}
        {/* Vincent B1011 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
            d'import CSV massif de tags sur des vidéos existantes.
            Réservé aux admins pour éviter les uploads accidentels. */}
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowTagsImportModal(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400"
            data-testid="search-toolbar-btn-import-csv"
            title={tr("Importer un fichier CSV ou Excel (.xlsx) pour ajouter des tags à plusieurs vidéos en masse", "Import a CSV or Excel (.xlsx) file to add tags to several videos at once")}
          >
            {tr("📥 Importer tags (CSV ou Excel)", "📥 Import tags (CSV or Excel)")}
          </button>
        )}
        {/* Vincent B1012 (Eric 2026-02-26 SPEC STRICK PROD) — Bouton
            historique + rollback des imports de tags. */}
        {isAdmin && (
          <button
            type="button"
            onClick={() => setShowTagsImportHistoryModal(true)}
            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded bg-white border border-zinc-300 text-zinc-700 hover:bg-zinc-50 hover:border-zinc-400"
            data-testid="search-toolbar-btn-import-history"
            title={tr("Voir l'historique des imports CSV/XLSX et annuler un import en 1 clic", "See the CSV/XLSX import history and undo an import in one click")}
          >
            {tr("📜 Historique imports", "📜 Import history")}
          </button>
        )}
        {/* B1100 — L'historique de fusion vit dans l'onglet « Gérer le
            vocabulaire » (sous chaque tag) : le bouton y amène directement. */}
        <button
          type="button"
          onClick={() => goToTab("vocabulaire")}
          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-400"
          data-testid="search-toolbar-btn-history"
          title={tr("Voir l'historique de fusion/renommage sous chaque tag", "See the merge/rename history under each tag")}
        >
          <ClockCounterClockwise size={13} weight="regular" />
          {tr("Historique fusions", "Merge history")}
        </button>
      </nav>

      {activeTab === "doublons" && (
        <div data-testid="search-tab-panel-doublons">
          <TagsPage embedded section="duplicates" />
        </div>
      )}
      {activeTab === "orphelins" && (
        <div data-testid="search-tab-panel-orphelins">
          <TagsPage embedded section="vocabulary" defaultOrphansOnly />
        </div>
      )}
      {activeTab === "vocabulaire" && (
        <div data-testid="search-tab-panel-vocabulaire">
          <TagsPage embedded section="vocabulary" />
        </div>
      )}
      {activeTab === "nuage" && (
        <div data-testid="search-tab-panel-nuage">
          <TagCloud />
        </div>
      )}

      {activeTab === "resultats" && (
      <div data-testid="search-tab-panel-resultats">

      {/* Barre de contrôle : tags actifs + toggle mode + ajout de tag. */}
      <div className="mb-4 flex flex-wrap items-center gap-2 bg-zinc-50 border border-zinc-200 rounded p-3">
        {tags.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-900 border border-blue-200 px-2 py-0.5 rounded-full"
            data-testid={`search-results-active-tag-${t}`}
          >
            {t}
            <button
              type="button"
              onClick={() => removeTag(t)}
              className="text-blue-600 hover:text-blue-900 font-bold"
              aria-label={`Retirer ${t}`}
              data-testid={`search-results-remove-${t}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          type="text"
          value={addingTag}
          onChange={(e) => setAddingTag(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && addingTag.trim()) { e.preventDefault(); addTag(addingTag.trim()); } }}
          placeholder={tr("+ Ajouter un tag (Entrée)", "+ Add a tag (Enter)")}
          className="text-xs px-2 py-1 outline-none bg-transparent border-b border-transparent focus:border-blue-400"
          data-testid="search-results-add-tag-input"
        />
        {tags.length > 1 && (
          <button
            type="button"
            onClick={switchMode}
            className="ml-auto text-[11px] font-semibold px-2 py-1 rounded bg-white border border-zinc-300 hover:bg-zinc-100"
            data-testid="search-results-mode-toggle"
            title={mode === "and"
              ? tr("Mode ET : documents portant TOUS les tags. Cliquer pour passer en OU.", "AND mode: documents carrying EVERY tag. Click to switch to OR.")
              : tr("Mode OU : documents portant AU MOINS UN tag. Cliquer pour passer en ET.", "OR mode: documents carrying AT LEAST ONE tag. Click to switch to AND.")}
          >
            {mode === "and" ? "ET" : "OU"}
          </button>
        )}
      </div>

      {/* Vincent B1100 (Eric 2026-09-07) — L'ancien panneau repliable
          « Vocabulaire des tags » (B991) rendu ici en bas de page est
          remplacé par les onglets dédiés en haut : « Gérer le vocabulaire »,
          « Purger les orphelins » et « Détecter les doublons ». Eric ne veut
          plus avoir à déplier une section de fusion pour y accéder. */}
      {/* Vincent B910 (Eric 2026-02-14 SPEC STRICK PROD) — Tags connexes :
          les tags qui apparaissent aux côtés de la sélection sur les
          vidéos de l'org. Cliquer élargit ou raffine la requête sans
          deviner. Masqué si aucun tag actif ou aucune co-occurrence. */}
      {tags.length > 0 && related.length > 0 && (
        <div
          className="mb-4 flex flex-wrap items-center gap-2 bg-violet-50 border border-violet-200 rounded p-3"
          data-testid="related-tags-panel"
          data-related-count={related.length}
        >
          <span className="text-[11px] font-semibold text-violet-900 whitespace-nowrap">
            {tr("✨ Tags recherchés avec ceux-ci :", "✨ Tags searched alongside these:")}
          </span>
          {related.map((r) => (
            <button
              key={r.tag}
              type="button"
              onClick={() => addTag(r.tag)}
              className="inline-flex items-center gap-1 text-[11px] bg-white border border-violet-300 text-violet-900 hover:bg-violet-100 rounded-full px-2 py-0.5 transition-colors"
              data-testid={`related-tag-chip-${r.tag}`}
              data-share-pct={r.share_pct}
              title={`Apparaît sur ${r.count} vidéo(s) parmi les résultats (${r.share_pct}%). Cliquer pour ajouter à la recherche.`}
            >
              <span className="font-medium">+ {r.tag}</span>
              <span className="text-[9px] font-mono text-violet-500 tabular-nums">
                {r.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-zinc-500 italic text-center py-8" data-testid="search-results-loading">⏳ Chargement…</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-zinc-500 italic text-center py-8 bg-white border border-zinc-200 rounded" data-testid="search-results-empty">
          {tags.length === 0
            ? tr("Ajoutez un tag pour lancer une recherche.", "Add a tag to start a search.")
            : tr("Aucun document ne correspond à ces tags. Essayez le mode OU ou retirez un tag.", "No document matches these tags. Try OR mode or remove a tag.")}
        </p>
      ) : (
        <div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3"
          data-testid="search-results-grid"
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => navigate(`/dashboard/video/${item.id}`)}
              className="text-left bg-white border border-zinc-200 rounded p-3 hover:border-blue-400 hover:shadow-sm transition-all"
              data-testid={`search-results-card-${item.id}`}
              data-kind={item.kind}
            >
              <div className="flex items-start gap-2 mb-2">
                <span className="text-lg" aria-hidden="true">
                  {item.kind === "text" ? "📄" : "🎬"}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-zinc-900 truncate">
                    {item.title || "(sans titre)"}
                  </h3>
                  {item.training_theme && (
                    <p className="text-[10px] text-zinc-500 uppercase tracking-wider">{item.training_theme}</p>
                  )}
                </div>
              </div>
              {item.description && (
                <p className="text-xs text-zinc-600 line-clamp-2 mb-2">{item.description}</p>
              )}
              {(item.tags || []).length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {(item.tags || []).slice(0, 8).map((t) => (
                    <span
                      key={t}
                      className={`text-[10px] px-1.5 py-0.5 rounded ${
                        tags.some((s) => s.toLowerCase() === t.toLowerCase())
                          ? "bg-blue-100 text-blue-800 font-semibold"
                          : "bg-zinc-100 text-zinc-600"
                      }`}
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      </div>
      )}

      {/* Vincent B1011 (Eric 2026-02-26 SPEC STRICK PROD) — Modale
          import CSV massif de tags sur vidéos existantes. */}
      <CsvImportModal
        open={showTagsImportModal}
        onClose={() => setShowTagsImportModal(false)}
        title={tr("Importer des tags (CSV ou Excel .xlsx)", "Import tags (CSV or Excel .xlsx)")}
        description={
          <div>
            {/* Tâche 1262, révision du 2026-06-29 (Eric) — Le texte annonçait
                d'import annonçait « deux colonnes toutes deux exigées » alors
                qu'une seule l'est désormais, et ne disait pas lesquelles sont
                optionnelles. */}
            {/* Tâche 1437 (Eric 2026-10-02) — Les NOMS DE COLONNES
                (`tag`, `video_id`, `langue`, `equivalent_fr`, `nombre`) ne
                sont PAS traduits : ce sont les en-têtes que le serveur
                reconnaît. Les traduire ferait écrire « number » dans un
                fichier dont la colonne serait alors ignorée en silence. */}
            <p className="mb-1">
              {tr("Fichier accepté : ", "Accepted file: ")}
              <strong>CSV</strong>{tr(" ou ", " or ")}<strong>Excel (.xlsx)</strong>
              {tr(" — aucune conversion préalable nécessaire. Une seule colonne est ",
                  " — no prior conversion needed. Only one column is ")}
              <strong>{tr("obligatoire", "required")}</strong>{tr(" : ", ": ")}
              <code className="bg-zinc-100 px-1 rounded">tag</code>.
            </p>
            <p className="mb-1 text-xs text-zinc-600">
              {tr("Colonnes ", "Optional ")}
              <strong>{tr("optionnelles", "columns")}</strong>{tr(" : ", ": ")}
              <code className="bg-zinc-100 px-1 rounded">video_id</code>{" "}
              {tr("(associe le tag à cette vidéo), ", "(links the tag to that video), ")}
              <code className="bg-zinc-100 px-1 rounded">{tr("langue", "langue")}</code>{" "}
              {tr("(fr ou en), ", "(fr or en), ")}
              <code className="bg-zinc-100 px-1 rounded">equivalent_fr</code>,{" "}
              <code className="bg-zinc-100 px-1 rounded">{tr("nombre", "nombre")}</code>{" "}
              {tr("(singulier ou pluriel).", "(singular or plural).")}
            </p>
            <p className="text-xs text-zinc-500">
              {tr(
                "Avec video_id : une ligne = une association tag → vidéo ; les video_id inconnus de ton organisation sont ignorés (sécurité). Sans video_id : les tags rejoignent le vocabulaire de l'organisation, sans être posés sur une vidéo. La ligne d'en-tête est facultative (ordre par défaut : tag, langue, equivalent_fr, nombre). Les tags vides ou de plus de 60 caractères sont ignorés, et un tag déjà connu n'est jamais dupliqué. Maximum 2000 lignes par fichier.",
                "With video_id: one row = one tag → video link; video_id values unknown to your organisation are ignored (safety). Without video_id: the tags join the organisation's vocabulary without being placed on a video. The header row is optional (default order: tag, langue, equivalent_fr, nombre). Empty tags and tags over 60 characters are ignored, and a tag already known is never duplicated. Maximum 2000 rows per file.",
              )}
            </p>
          </div>
        }
        templateCsv={"tag,video_id,langue,equivalent_fr,nombre\nretraite,,fr,,singulier\nfiscalité,<video-uuid-1>,fr,,singulier\nREER,<video-uuid-2>,fr,,singulier\n"}
        templateFilename="modele-tags.csv"
        onSubmit={submitTagsImport}
        resultRenderer={(r) => (
          <div>
            <p className="font-semibold">{tr("Import terminé.", "Import complete.")}</p>
            {r.mode === "vocabulary" ? (
              <ul className="text-xs mt-1 space-y-0.5">
                <li>{tr("Aucune colonne video_id : tags versés au vocabulaire.", "No video_id column: tags added to the vocabulary.")}</li>
                <li>{tr("Tags ajoutés :", "Tags added:")} <b>{r.added ?? 0}</b></li>
                <li>{tr("Tags mis à jour :", "Tags updated:")} <b>{r.updated ?? 0}</b></li>
                <li>{tr("Lignes traitées :", "Rows processed:")} <b>{r.processed ?? 0}</b></li>
              </ul>
            ) : (
              <ul className="text-xs mt-1 space-y-0.5">
                <li>{tr("Vidéos mises à jour :", "Videos updated:")} <b>{r.videos_updated ?? 0}</b></li>
                <li>{tr("Associations soumises :", "Links submitted:")} <b>{r.added ?? 0}</b></li>
                <li>{tr("Sur vidéos inconnues :", "On unknown videos:")} <b>{r.skipped_missing ?? 0}</b></li>
                <li>{tr("Lignes invalides :", "Invalid rows:")} <b>{r.skipped_invalid ?? 0}</b></li>
                <li>{tr("Lignes traitées :", "Rows processed:")} <b>{r.processed ?? 0}</b></li>
              </ul>
            )}
          </div>
        )}
        dataTestidPrefix="tags-csv-import"
        lang={lang}
      />

      {/* Vincent B1012 (Eric 2026-02-26 SPEC STRICK PROD) — Modale
          historique + rollback des imports de tags vidéos. */}
      <ImportHistoryModal
        open={showTagsImportHistoryModal}
        onClose={() => setShowTagsImportHistoryModal(false)}
        type="video_tags"
        dataTestidPrefix="tags-import-history"
        lang={lang}
      />
    </div>
  );
}
